// src/components/AuthModal/WalletOnboarding.jsx
// Onboarding GUIADO de creación de wallet (plan §2): crea la wallet embebida sin
// mostrar semillas, con un mini-explicador por paso que le quita el miedo al
// usuario. Tono StoryBrand: el usuario es el héroe, esto es su primer paso soberano.
//
// 3 pasos educativos → 1 paso de acción que dispara el flujo SIWE (hook compartido)
// y muestra el estado en vivo. Se monta solo con REACT_APP_PRIVY_APP_ID (lo decide
// AuthModal), así los hooks de Privy no corren sin su provider.
import React, { useState, useEffect } from 'react';
import {
  FiArrowRight, FiArrowLeft, FiKey, FiLifeBuoy, FiShield, FiCheck, FiX,
} from 'react-icons/fi';
import { useWalletLogin } from '../../lib/useWalletLogin';

// Pasos educativos — responden, antes de crear nada, las dudas que frenan a la gente.
const STEPS = [
  {
    Icon: FiShield,
    title: 'Creá tu caja fuerte digital',
    body: 'Una wallet es tu propia caja fuerte para tu plata. La creás acá en 30 segundos: sin frases de 12 palabras que memorizar y sin instalar nada.',
  },
  {
    Icon: FiKey,
    title: 'Vos tenés la única llave',
    body: 'Tu plata vive en tu wallet, no en Bitplify. Ni nosotros ni nadie puede sacarla. Por eso es más seguro que dejarla en una empresa que la custodia por vos.',
  },
  {
    Icon: FiLifeBuoy,
    title: '¿Y si pierdo el teléfono?',
    body: 'No la perdés. Se recupera con tu huella o Face ID (passkey) y con recuperación social: personas o dispositivos de confianza que vos elegís. Nada de papelitos con claves.',
  },
];

export default function WalletOnboarding({ onSuccess, onBack }) {
  const [step, setStep] = useState(0);
  const { start, status, error, ready, busy } = useWalletLogin(onSuccess);

  const isAction = step === STEPS.length; // último paso = crear
  const total    = STEPS.length + 1;

  // Cuando la sesión queda lista, cerramos (onSuccess ya se llamó en el hook).
  useEffect(() => { if (status === 'done') onSuccess?.(); }, [status, onSuccess]);

  const next = () => setStep(s => Math.min(s + 1, STEPS.length));
  const prev = () => (step === 0 ? onBack?.() : setStep(s => s - 1));

  const actionLabel = status === 'connecting' ? 'Conectando…'
                    : status === 'signing'    ? 'Confirmá en tu wallet…'
                    : status === 'verifying'  ? 'Casi listo…'
                    : status === 'error'      ? 'Reintentar'
                    : 'Crear mi wallet';

  return (
    <div className="wo">
      {/* Progreso */}
      <div className="wo-dots" role="progressbar" aria-valuenow={step + 1} aria-valuemax={total}>
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className={`wo-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} />
        ))}
      </div>

      {!isAction ? (
        <div className="wo-step" key={step}>
          <div className="wo-ic">{React.createElement(STEPS[step].Icon)}</div>
          <h3 className="wo-title">{STEPS[step].title}</h3>
          <p className="wo-body">{STEPS[step].body}</p>

          <div className="wo-nav">
            <button type="button" className="wo-back" onClick={prev}>
              <FiArrowLeft /> {step === 0 ? 'Volver' : 'Atrás'}
            </button>
            <button type="button" className="wo-next" onClick={next}>
              Siguiente <FiArrowRight />
            </button>
          </div>
        </div>
      ) : (
        <div className="wo-step wo-step-action" key="action">
          <div className="wo-ic wo-ic-glow"><FiKey /></div>
          <h3 className="wo-title">Todo listo para crear tu wallet</h3>
          <p className="wo-body">
            Al continuar, creás tu wallet y firmás un mensaje que prueba que sos vos.
            <strong> Firmar para entrar no mueve ni un centavo</strong> de tus fondos.
          </p>

          {error && <div className="am-error"><FiX /> {error}</div>}

          <button
            type="button"
            className="am-wallet-btn wo-create"
            onClick={start}
            disabled={!ready || busy}
          >
            {busy ? <span className="am-spin" /> : <>{actionLabel} <FiArrowRight /></>}
          </button>

          <ul className="am-wallet-points wo-points">
            <li><FiCheck /> Tu plata, tu llave — autocustodia desde el primer segundo.</li>
            <li><FiCheck /> Sin semillas ni contraseñas que perder.</li>
          </ul>

          {!busy && (
            <button type="button" className="wo-back wo-back-action" onClick={prev}>
              <FiArrowLeft /> Atrás
            </button>
          )}
        </div>
      )}
    </div>
  );
}
